import type {
  AlertaFicha, AlertaLinhaCusto, CentroClassificacao, CentroNatureza, ComponenteLoteDto, ComponenteResponse, ComponenteSimularDto, EtapaProcessoResponse,
  FichaResponse, ItemResponse, LinhaSimuladaResponse, MaterialFichaResponse, OrigemCustoMaterial, PerdaModo, RendimentoGrupo,
} from '@triade/contracts';
import { percentualParaTaxa, rendimentoProcessoSchema, SITUACOES_PAINEL_FICHA, type SituacaoPainelFicha } from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo, fracaoParaPercentualTexto, formatarNumeroCompleto, formatarQuantidade } from '@/lib/format';

/** Rotas da tela: `/custos/fichas-tecnicas?itemId=` (resolve a versao) e `/custos/fichas-tecnicas/$id`. */
export const ROTA_FICHAS = '/custos/fichas-tecnicas';

export type TipoComponente = ComponenteResponse['tipo'];

export const TIPOS_COMPONENTE: TipoComponente[] = ['MATERIAL', 'MAO_DE_OBRA', 'AQUISICAO', 'OUTRO'];

export const ROTULO_TIPO_COMPONENTE: Record<TipoComponente, string> = {
  MATERIAL: 'Material',
  MAO_DE_OBRA: 'Mão de obra',
  AQUISICAO: 'Aquisição',
  OUTRO: 'Outro',
  COPRODUTO: 'Coproduto',
};

/** Rotulo do tipo do item componente (selo da linha da lista de materiais). */
export const ROTULO_ITEM_TIPO: Partial<Record<string, string>> = {
  MATERIA_PRIMA: 'Matéria-prima',
  MATERIAL_SECUNDARIO: 'Material secundário',
  MATERIAL_AUXILIAR: 'Material auxiliar',
  EMBALAGEM: 'Embalagem',
  SERVICO_TERCEIRO: 'Serviço de terceiro',
  MERCADORIA: 'Mercadoria',
};

/**
 * Selo da linha da lista unica de componentes. Semiacabado e coproduto vem da origem do custo (ficha do componente);
 * o resto pelo tipo do item. Sem item (ou tipo sem rotulo) = "Outro". `destaque` = semiacabado e coproduto.
 */
export function seloComponente(l: Pick<LinhaFicha, 'origemCusto' | 'itemTipo' | 'servicoTerceiro'>): { rotulo: string; destaque: boolean } {
  if (l.origemCusto === 'SEMIACABADO') return { rotulo: 'Semiacabado', destaque: true };
  if (l.origemCusto === 'COPRODUTO') return { rotulo: 'Coproduto', destaque: true };
  const tipoItem = l.itemTipo ?? (l.servicoTerceiro ? 'SERVICO_TERCEIRO' : null);
  const rotulo = tipoItem ? ROTULO_ITEM_TIPO[tipoItem] : undefined;
  return { rotulo: rotulo ?? 'Outro', destaque: false };
}

/** Linha de semiacabado ou coproduto: o custo vem da ficha referenciada (ou do rateio), nao e editado na grade. */
export function travadaNaGrade(l: Pick<LinhaFicha, 'origemCusto'>): boolean {
  return l.origemCusto === 'SEMIACABADO' || l.origemCusto === 'COPRODUTO';
}

/**
 * Tipo como vai para a API. COPRODUTO nao e tipo que a API aceita na simulacao/gravacao: a ficha de coproduto nao simula
 * nem grava (ver ficha-page), so a assinatura o compara; ali vira OUTRO.
 */
function tipoEnviado(t: TipoComponente): ComponenteSimularDto['tipo'] {
  return t === 'COPRODUTO' ? 'OUTRO' : t;
}

/**
 * Tipos que a secao Materiais aceita por tipo do item. Produto nao tem mao de obra fora do processo (o tempo vive so nas
 * etapas); servico e mercadoria mantem a linha (composicao de horas, sem centro).
 */
export function tiposDeMaterial(tipoItem: ItemResponse['tipo'] | undefined): TipoComponente[] {
  return tipoItem === 'PRODUTO' ? TIPOS_COMPONENTE.filter((t) => t !== 'MAO_DE_OBRA') : TIPOS_COMPONENTE;
}

export const ROTULO_ALERTA: Record<AlertaFicha, string> = {
  QUANTIDADE_ZERO: 'Quantidade 0',
  QUANTIDADE_INVALIDA: 'Quantidade inválida',
  CUSTO_AUSENTE: 'Custo ausente',
  CUSTO_INVALIDO: 'Custo inválido',
  DESCRICAO_AUSENTE: 'Sem descrição',
  PERDA_INVALIDA: 'Perda inválida',
};

/** Modos de perda da linha de material (Sessao 16), na ordem do seletor. */
export const PERDA_MODOS: PerdaModo[] = ['NENHUM', 'SOBRE_SAIDA', 'SOBRE_ENTRADA', 'RENDIMENTO', 'CONSUMO_TOTAL'];

export const ROTULO_PERDA_MODO: Record<PerdaModo, string> = {
  NENHUM: 'Sem perda',
  SOBRE_SAIDA: 'Sobre a saída (1+p)',
  SOBRE_ENTRADA: 'Sobre a entrada (1/(1−p))',
  RENDIMENTO: 'Rendimento (1/r)',
  CONSUMO_TOTAL: 'Consumo total direto',
};

/** Grupos de insumos que recebem o rendimento do processo (seletor da ficha). */
export const ROTULO_GRUPO_RENDIMENTO: Record<RendimentoGrupo, string> = {
  MATERIA_PRIMA: 'Matéria-prima',
  MATERIAL_SECUNDARIO: 'Material secundário',
  EMBALAGEM: 'Embalagem',
  MATERIAL_AUXILIAR: 'Material auxiliar',
  SERVICO_TERCEIRO: 'Serviço de terceiro',
  TODOS_INSUMOS: 'Todos os insumos',
};

export const GRUPOS_RENDIMENTO: RendimentoGrupo[] = ['MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO', 'TODOS_INSUMOS'];

export const ROTULO_AVISO_LINHA: Record<AlertaLinhaCusto, string> = {
  CUSTO_CONGELADO_SEMIACABADO: 'Custo congelado: semiacabado sem ficha escolhida',
  CENTRO_INVALIDO: 'Centro inválido',
  ETAPA_TIPO_INVALIDO: 'Etapa só em mão de obra',
  CPM_AUSENTE: 'Sem CPM',
  CPM_ANTIGO: 'CPM antigo',
  CICLO_NA_FICHA: 'Ciclo na cascata',
  PROFUNDIDADE_FICHA_EXCEDIDA: 'Cascata profunda demais',
  AQUISICAO_AUSENTE: 'Insumo sem custo de aquisição',
  SAIDA_INVALIDA: 'Saída com quantidade ou valor inválido',
};

/**
 * Motivo DA SUBFICHA de um aviso da linha (Sessao 49): o texto que a API mandou para o codigo (ex.: "B-GRANEL: insumo X sem custo de
 * aquisicao vigente na data"). Sem `motivos` na linha, ou sem o codigo, undefined: a tela mostra o rotulo generico.
 */
export function motivoDoAviso(sim: Pick<LinhaSimuladaResponse, 'motivos'> | undefined, codigo: AlertaLinhaCusto): string | undefined {
  return sim?.motivos?.find((m) => m.code === codigo)?.message;
}

/** Mensagens da API que nao carregam texto util para o usuario (o code aparece no lugar). */
export const ROTULO_ERRO_CUSTO: Record<string, string> = {
  CICLO_NA_FICHA: 'Há um ciclo na cascata de semiacabados: uma ficha usa, direta ou indiretamente, a si mesma.',
  PROFUNDIDADE_FICHA_EXCEDIDA: 'A cascata de semiacabados passa de 10 níveis. Simplifique a estrutura.',
};

/** AAAA-MM (competencia do CPM) como MM/AAAA; o que nao casa volta igual. */
export function mesCurto(competencia: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})$/.exec(competencia ?? '');
  return m ? `${m[2]}/${m[1]}` : (competencia ?? '');
}

/** Centro ANALITICO para a etapa ou o centro de consumo (GET /centros-custo/analiticos): caminho e nome. */
export interface CentroOpcao {
  id: string;
  codigo: string;
  nome: string;
  /** Caminho na arvore ("3.1"). */
  caminho: string;
  /** Natureza e classificacao do analitico: decidem se ele aceita ETAPA (centroAceitaEtapa, no contrato). */
  natureza: CentroNatureza | null;
  classificacao: CentroClassificacao | null;
}

/** CPM e custo da etapa como a API GRAVOU (processo.etapas da ficha salva). Sem R$ para o PCP: os campos nao existem. */
export interface EtapaGravada {
  competencia?: string;
  cpmAntigo: boolean;
  cpm?: string;
  custo?: string;
}

/** Secao da linha: etapa do processo (com centro, ou em branco a escolher) ou material (inclui mao de obra legada). */
export type SecaoLinha = 'processo' | 'materiais';

/** Linha da grade. Quantidade e custo ficam como o usuario digita (texto pt-BR); a conversao para a API so troca virgula por ponto. */
export interface LinhaFicha {
  uid: string;
  /** Id do componente na API (vazio em linha nova). Liga a linha ao `processo.etapas` gravado. */
  componenteId: string | null;
  secao: SecaoLinha;
  tipo: TipoComponente;
  descricao: string;
  componenteItemId: string | null;
  custoAquisicaoId: string | null;
  /** Codigo do item componente (so exibicao). */
  itemCodigo: string | null;
  /** Etapa de roteiro: centro de custo analitico (a `quantidade` e minutos). Nulo = linha de material. */
  centroCustoId: string | null;
  /** Centro de consumo opcional de um material (informativo, fora do custo). */
  centroConsumoId: string | null;
  unidade: string;
  quantidade: string;
  /** Vazio = a API resolve (insumo com ficha escolhida, custo de aquisicao). */
  custoUnitario: string;
  /** CPM e custo da etapa como estao GRAVADOS. Some quando a linha muda de centro ou de minutos (ja nao e o gravado). */
  etapaGravada?: EtapaGravada;
  /** Perda da linha de material (Sessao 16). `perdaValor` e o texto digitado: percentual (ex.: "20") ou decimal no consumo total. */
  perdaModo: PerdaModo;
  perdaValor: string;
  /** Consumo total que a API devolveu para a linha GRAVADA (some ao editar quantidade ou perda). */
  consumoTotal?: string;
  /** Item SERVICO_TERCEIRO (so vem da ficha gravada). */
  servicoTerceiro?: boolean;
  /** Fornecedor fixado na aquisicao deste material comprado (Sessao 17). Nulo = a preferencial. */
  fornecedorId?: string | null;
  /** Tipo do item componente (selo). So vem da ficha gravada ou da busca no cadastro. */
  itemTipo?: string | null;
  /** Origem do custo da linha (so da ficha gravada): semiacabado e coproduto nao sao editados na grade. */
  origemCusto?: OrigemCustoMaterial;
  /** Ficha do semiacabado, ou do item principal do coproduto (link "Abrir ficha"). */
  fichaId?: string | null;
  /** Custo da linha como a API devolveu na ficha (so com custo.ver). Usado nas linhas de semiacabado e coproduto. */
  custoFicha?: { custoUnitario?: string; custoTotal?: string };
}

/** Campos que a grade edita. */
export type CampoEdicao = ColunaEditavel | 'centroCustoId' | 'centroConsumoId' | 'perdaModo' | 'perdaValor' | 'fornecedorId';

let contador = 0;
export function novoUid(): string {
  contador += 1;
  return `l${contador}`;
}

export function novaLinha(parcial: Partial<Omit<LinhaFicha, 'uid'>> = {}): LinhaFicha {
  return {
    uid: novoUid(),
    componenteId: null,
    secao: 'materiais',
    tipo: 'MATERIAL',
    descricao: '',
    componenteItemId: null,
    custoAquisicaoId: null,
    itemCodigo: null,
    centroCustoId: null,
    centroConsumoId: null,
    unidade: '',
    quantidade: '',
    custoUnitario: '',
    perdaModo: 'NENHUM',
    perdaValor: '',
    ...parcial,
  };
}

/** Tipo de componente que cada tipo de item vira ao entrar na ficha. */
export const TIPO_DO_ITEM: Record<ItemResponse['tipo'], TipoComponente> = {
  PRODUTO: 'MATERIAL',
  MERCADORIA: 'AQUISICAO',
  SERVICO: 'MAO_DE_OBRA',
  MATERIA_PRIMA: 'AQUISICAO',
  MATERIAL_SECUNDARIO: 'AQUISICAO',
  EMBALAGEM: 'AQUISICAO',
  MATERIAL_AUXILIAR: 'AQUISICAO',
  SERVICO_TERCEIRO: 'AQUISICAO',
};

/**
 * Troca o item componente de uma linha de material (Sessao 37): item, tipo, unidade e descricao passam a ser os do item escolhido; o que dependia do
 * item anterior (consumo gravado, fornecedor fixado, custo digitado de aquisicao) sai. Quantidade, perda e centro de consumo ficam.
 */
export function linhaComItem(linha: LinhaFicha, item: Pick<ItemResponse, 'id' | 'codigo' | 'descricao' | 'tipo' | 'unidade'>): LinhaFicha {
  const tipo = TIPO_DO_ITEM[item.tipo];
  return {
    ...linha,
    tipo,
    descricao: item.descricao,
    componenteItemId: item.id,
    custoAquisicaoId: null,
    itemCodigo: item.codigo,
    itemTipo: item.tipo,
    unidade: item.unidade ?? '',
    ...(tipo === 'AQUISICAO' ? { custoUnitario: '' } : {}),
    fornecedorId: null,
    servicoTerceiro: undefined,
    consumoTotal: undefined,
    origemCusto: undefined,
    fichaId: undefined,
    custoFicha: undefined,
  };
}

/** Etapa nova: mao de obra em minutos, a escolher o centro analitico. */
export function novaEtapa(): LinhaFicha {
  return novaLinha({ secao: 'processo', tipo: 'MAO_DE_OBRA', unidade: 'min' });
}

/** Linha de composicao de horas do SERVICO: mao de obra em horas, sem centro (o motor soma horas x custo/hora). */
export function novaHora(): LinhaFicha {
  return novaLinha({ secao: 'materiais', tipo: 'MAO_DE_OBRA', unidade: 'h' });
}

/** Decimal da API ("88.000000") como texto de campo pt-BR ("88,00"): so remove zeros de escala e troca o separador. */
export function textoCampoDecimal(valor: string): string {
  return formatarNumeroCompleto(valor).replace(/^—$/, '');
}

/**
 * Linhas da versao gravada. `etapas` = `processo.etapas` (ligadas pelo `componenteId`): so com custo.ver o CPM e o custo
 * vem; `competencia` e `cpmAntigo` vem sempre. Sem R$ (PCP), nada de `etapaGravada.cpm`/`custo`.
 */
export function deResposta(
  componentes: ComponenteResponse[] | undefined,
  etapas: EtapaProcessoResponse[] = [],
  servicosTerceiros: ReadonlySet<string> = new Set(),
): LinhaFicha[] {
  return (componentes ?? []).map((c) => {
    const etapa = c.centroCustoId ? etapas.find((e) => e.componenteId === c.id) : undefined;
    const perdaModo = c.perdaModo ?? 'NENHUM';
    return novaLinha({
      perdaModo,
      // A API guarda a perda como fracao (percentuais) ou decimal (consumo total): o texto de campo segue o modo.
      perdaValor: perdaModo === 'NENHUM' || !c.perdaValor ? '' : perdaModo === 'CONSUMO_TOTAL' ? decimalParaTextoCampo(c.perdaValor) : fracaoParaPercentualTexto(c.perdaValor),
      consumoTotal: c.consumoTotal,
      ...(servicosTerceiros.has(c.id) ? { servicoTerceiro: true } : {}),
      componenteId: c.id,
      secao: c.centroCustoId ? 'processo' : 'materiais',
      tipo: c.tipo,
      descricao: c.descricao,
      componenteItemId: c.componenteItemId,
      custoAquisicaoId: c.custoAquisicaoId,
      fornecedorId: c.fornecedorId ?? null,
      itemCodigo: c.componenteItemCodigo ?? null,
      centroCustoId: c.centroCustoId ?? null,
      centroConsumoId: c.centroConsumoId ?? null,
      unidade: c.unidade ?? '',
      quantidade: formatarQuantidade(c.quantidade) === '—' ? '' : formatarQuantidade(c.quantidade),
      // AQUISICAO: o custo e o do motor (aquisicao vigente), nao digitado. Etapa: o custo vem do CPM (minutos x CPM).
      custoUnitario: c.tipo === 'AQUISICAO' || c.centroCustoId ? '' : textoCampoDecimal(c.custoUnitario ?? ''),
      ...(etapa
        ? { etapaGravada: { competencia: etapa.competencia, cpmAntigo: etapa.cpmAntigo === true, cpm: etapa.cpm, custo: etapa.custo } }
        : {}),
    });
  });
}

/** Linhas de uma ficha da API: etapas do `processo` (com CPM e custo gravados quando a API manda) e materiais. */
export function deFicha(ficha: FichaResponse): LinhaFicha[] {
  const materiais = new Map<string, MaterialFichaResponse>((ficha.materiais?.itens ?? []).map((m) => [m.componenteId, m]));
  const servicos = new Set((ficha.materiais?.itens ?? []).filter((m) => m.servicoTerceiro).map((m) => m.componenteId));
  return deResposta(ficha.componentes, ficha.processo?.etapas ?? [], servicos).map((l) => {
    const m = l.componenteId ? materiais.get(l.componenteId) : undefined;
    if (!m) return l;
    return {
      ...l,
      itemTipo: m.itemTipo ?? null,
      origemCusto: m.origemCusto,
      fichaId: m.fichaId ?? null,
      custoFicha: { custoUnitario: m.custoUnitario, custoTotal: m.custoTotal },
    };
  });
}

function paraApi(texto: string): string {
  return decimalDigitado(texto) ?? texto.trim();
}

/**
 * Perda da linha de material como a API espera. Percentual (saida, entrada, rendimento) vira fracao SO por `percentualParaTaxa`;
 * consumo total e decimal de quantidade. Texto invalido vai como esta: a API classifica em `PERDA_INVALIDA`.
 * Etapa nao tem perda (o tempo recebe so o rendimento do processo).
 */
export function perdaParaApi(l: LinhaFicha): { perdaModo?: PerdaModo; perdaValor?: string } {
  if (l.secao === 'processo' || l.perdaModo === 'NENHUM') return {};
  const texto = l.perdaValor.trim();
  if (l.perdaModo === 'CONSUMO_TOTAL') return { perdaModo: l.perdaModo, perdaValor: decimalDigitado(texto) ?? texto };
  return { perdaModo: l.perdaModo, perdaValor: percentualParaTaxa(texto) ?? texto };
}

/** Mensagem de perda que nao pode ser gravada (vazia ou fora do formato); null = ok. Validacao de formato, nao calculo. */
export function perdaErro(l: LinhaFicha): string | null {
  if (l.secao === 'processo' || l.perdaModo === 'NENHUM') return null;
  const texto = l.perdaValor.trim();
  if (texto === '') return 'Informe o valor da perda';
  const ok = l.perdaModo === 'CONSUMO_TOTAL' ? decimalDigitado(texto) !== null : percentualParaTaxa(texto) !== null;
  return ok ? null : l.perdaModo === 'CONSUMO_TOTAL' ? 'Perda inválida: use um decimal (ex.: 1,5)' : 'Perda inválida: use um percentual (ex.: 20)';
}

/** Rendimento do processo digitado (percentual) como a API espera; vazio = sem. `erro` quando nao cabe no contrato. */
export function rendimentoParaApi(r: RendimentoTexto): { rendimentoProcesso: string | null; rendimentoGrupo: RendimentoGrupo; erro: string | null } {
  const texto = r.processo.trim();
  if (texto === '') return { rendimentoProcesso: null, rendimentoGrupo: r.grupo, erro: null };
  const taxa = percentualParaTaxa(texto);
  const valido = taxa !== null && rendimentoProcessoSchema.safeParse(taxa).success;
  return valido
    ? { rendimentoProcesso: taxa, rendimentoGrupo: r.grupo, erro: null }
    : { rendimentoProcesso: null, rendimentoGrupo: r.grupo, erro: 'Rendimento inválido: use um percentual maior que 0 e até 100 (ex.: 95)' };
}

/** Rendimento do processo como o usuario digita (percentual) e o grupo que o recebe. */
export interface RendimentoTexto {
  processo: string;
  grupo: RendimentoGrupo;
}

/** Rendimento gravado na ficha, em texto de campo (vazio = sem rendimento). */
export function rendimentoDe(ficha: FichaResponse): RendimentoTexto {
  return { processo: fracaoParaPercentualTexto(ficha.rendimentoProcesso), grupo: ficha.rendimentoGrupo ?? 'MATERIA_PRIMA' };
}

/**
 * Frase do consumo de uma linha gravada: a quantidade, o modo e o percentual digitados e o consumo total que a API devolveu.
 * Nada aqui calcula: o total vem de `consumoTotal`. Sem consumo gravado (linha nova ou editada) ou sem perda: null.
 */
export function fraseConsumo(l: LinhaFicha): string | null {
  if (l.secao === 'processo' || l.perdaModo === 'NENHUM' || !l.consumoTotal) return null;
  const total = formatarQuantidade(l.consumoTotal);
  const q = formatarQuantidade(decimalDigitado(l.quantidade));
  const p = l.perdaValor.trim();
  switch (l.perdaModo) {
    case 'SOBRE_SAIDA':
      return `${q} + ${p}% sobre a saída = ${total}`;
    case 'SOBRE_ENTRADA':
      return `${q} sobre a entrada com ${p}% de perda = ${total}`;
    case 'RENDIMENTO':
      return `${q} ÷ rendimento de ${p}% = ${total}`;
    case 'CONSUMO_TOTAL':
      return `Consumo total direto = ${total}`;
    default:
      return null;
  }
}

/** Corpo de POST /fichas-tecnicas/simular (tolerante): a API classifica o que faltar em `alertas`. */
export function paraSimular(linhas: LinhaFicha[]): ComponenteSimularDto[] {
  return linhas.map((l) => ({
    tipo: tipoEnviado(l.tipo),
    ...(l.componenteItemId ? { componenteItemId: l.componenteItemId } : {}),
    ...(l.custoAquisicaoId ? { custoAquisicaoId: l.custoAquisicaoId } : {}),
    ...(l.centroCustoId ? { centroCustoId: l.centroCustoId } : {}),
    ...(l.centroConsumoId && !l.centroCustoId ? { centroConsumoId: l.centroConsumoId } : {}),
    descricao: l.descricao,
    ...(l.unidade.trim() ? { unidade: l.unidade.trim() } : {}),
    quantidade: paraApi(l.quantidade),
    ...(l.tipo !== 'AQUISICAO' && !l.centroCustoId && l.custoUnitario.trim() ? { custoUnitario: paraApi(l.custoUnitario) } : {}),
    ...perdaParaApi(l),
    // Fornecedor fixado na linha (AQUISICAO ou MATERIAL comprado, como o importador grava): nunca se perde ao simular ou salvar; vazio = a preferencial (a API resolve).
    ...((l.tipo === 'AQUISICAO' || l.tipo === 'MATERIAL') && l.fornecedorId ? { fornecedorId: l.fornecedorId } : {}),
  }));
}

/** Corpo de PUT componentes / nova versao (a API valida de novo e responde 422 se algo nao puder ser gravado). */
export function paraLote(linhas: LinhaFicha[]): ComponenteLoteDto[] {
  return paraSimular(linhas).map((l) => ({
    tipo: l.tipo,
    ...(l.componenteItemId ? { componenteItemId: l.componenteItemId } : {}),
    ...(l.custoAquisicaoId ? { custoAquisicaoId: l.custoAquisicaoId } : {}),
    ...(l.centroCustoId ? { centroCustoId: l.centroCustoId } : {}),
    ...(l.centroConsumoId ? { centroConsumoId: l.centroConsumoId } : {}),
    descricao: (l.descricao ?? '').trim(),
    ...(l.unidade ? { unidade: l.unidade } : {}),
    quantidade: l.quantidade,
    ...(l.custoUnitario ? { custoUnitario: l.custoUnitario } : {}),
    ...(l.perdaModo !== undefined && l.perdaModo !== 'NENHUM' ? { perdaModo: l.perdaModo, perdaValor: l.perdaValor } : {}),
    ...(l.fornecedorId ? { fornecedorId: l.fornecedorId } : {}),
  }));
}

/** Impressao digital do que seria gravado: compara a grade com a versao carregada (sujo = diferente). */
export function assinatura(linhas: LinhaFicha[], descricao: string, rendimento: RendimentoTexto = { processo: '', grupo: 'MATERIA_PRIMA' }): string {
  return JSON.stringify({ d: descricao.trim(), l: paraSimular(linhas), r: rendimento });
}

/** Colunas editaveis de MATERIAIS, na ordem em que Tab e a colagem as percorrem. */
export const COLUNAS_EDITAVEIS = ['tipo', 'descricao', 'quantidade', 'unidade', 'custoUnitario'] as const;
/** Colunas editaveis do PROCESSO (etapa): descricao e minutos; o centro tem o seu editor de busca. */
export const COLUNAS_ETAPA = ['descricao', 'quantidade'] as const;
/** Colunas editaveis da composicao de horas (SERVICO): profissional ou centro, horas e custo por hora. */
export const COLUNAS_HORAS = ['descricao', 'quantidade', 'custoUnitario'] as const;
export type ColunaEditavel = (typeof COLUNAS_EDITAVEIS)[number];
/** Secao que recebe a colagem: as duas grades de linha e a composicao de horas. */
export type SecaoColagem = SecaoLinha | 'horas';

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** "Mão de obra", "mao de obra" ou "MAO_DE_OBRA" -> MAO_DE_OBRA; desconhecido -> null. */
export function lerTipoComponente(texto: string): TipoComponente | null {
  const t = semAcento(texto).replace(/_/g, ' ');
  return TIPOS_COMPONENTE.find((x) => semAcento(ROTULO_TIPO_COMPONENTE[x]) === t || x.toLowerCase().replace(/_/g, ' ') === t) ?? null;
}

/** Texto copiado do Excel/Planilhas (TSV) em matriz; ignora a ultima quebra de linha. */
export function lerColagem(texto: string): string[][] {
  const limpo = texto.replace(/\r\n?/g, '\n').replace(/\n$/, '');
  if (limpo === '') return [];
  return limpo.split('\n').map((l) => l.split('\t'));
}

/**
 * Aplica a colagem a partir da celula focada (linha, coluna editavel), acrescentando linhas quando passa do fim. `secao`
 * define a linha nova (etapa ou material) e as colunas que a colagem percorre.
 */
export function aplicarColagem(
  linhas: LinhaFicha[],
  linhaInicial: number,
  coluna: string,
  matriz: string[][],
  secao: SecaoColagem = 'materiais',
): LinhaFicha[] {
  const colunas: readonly string[] = secao === 'processo' ? COLUNAS_ETAPA : secao === 'horas' ? COLUNAS_HORAS : COLUNAS_EDITAVEIS;
  const resultado = linhas.map((l) => ({ ...l }));
  const c0 = colunas.indexOf(coluna);
  matriz.forEach((celulas, r) => {
    const i = linhaInicial + r;
    while (resultado.length <= i) resultado.push(secao === 'processo' ? novaEtapa() : secao === 'horas' ? novaHora() : novaLinha());
    const alvo = resultado[i] as LinhaFicha;
    alvo.etapaGravada = undefined; // a linha colada nao e mais o gravado (minutos/centro podem ter mudado)
    alvo.consumoTotal = undefined; // e o consumo gravado tambem nao vale mais
    celulas.forEach((bruto, c) => {
      const col = colunas[c0 + c];
      if (!col) return;
      const v = bruto.trim();
      if (col === 'tipo') {
        const t = lerTipoComponente(v);
        if (t) alvo.tipo = t;
      } else if (col === 'descricao' || col === 'quantidade' || col === 'unidade' || col === 'custoUnitario') {
        alvo[col] = v;
      }
    });
  });
  return resultado;
}

/** Lista de fichas (painel): tamanho da pagina. */
export const TAMANHO_PAGINA_FICHAS = 50;

export const ROTULO_TIPO_PAINEL_FICHA: Record<'PRODUTO' | 'SERVICO', string> = { PRODUTO: 'Produto', SERVICO: 'Serviço' };
export const TIPOS_PAINEL_FICHA = ['PRODUTO', 'SERVICO'] as const;

/**
 * Motivo (estadoCusto da API) de a ficha escolhida nao ter custo, em portugues. Codigos sem texto caem no padrao (ver `motivoCustoIndisponivel`).
 */
export const MOTIVO_CUSTO_INDISPONIVEL: Record<string, string> = {
  CPM_AUSENTE: 'centro sem custo por minuto',
  SEM_CPM: 'centro sem custo por minuto',
  AQUISICAO_AUSENTE: 'insumo sem custo de aquisição',
  CICLO_NA_FICHA: 'ciclo na cascata',
  SAIDA_INVALIDA: 'saídas deixam o custo negativo',
  PROFUNDIDADE_FICHA_EXCEDIDA: 'cascata profunda demais',
};

export function motivoCustoIndisponivel(estado: string | null | undefined): string {
  return (estado && MOTIVO_CUSTO_INDISPONIVEL[estado]) || 'a ficha não tem custo calculável';
}

/** Busca da rota `/custos/fichas-tecnicas`: `itemId` abre a ficha (ou a lista, sem ele) e o resto filtra a lista do painel. */
export interface BuscaFicha {
  itemId?: string;
  q?: string;
  situacao?: SituacaoPainelFicha;
  /** Categoria (com as subcategorias) ou `SEM`. */
  categoriaId?: string;
  tipo?: 'PRODUTO' | 'SERVICO';
  pagina?: number;
}

const ID_SEGURO = /^[A-Za-z0-9_-]{1,64}$/;

/** Texto da URL (o router faz JSON.parse: `?q=10874` chega como numero). Vazio ou nao-texto vira undefined. */
function textoDaUrl(v: unknown, max: number): string | undefined {
  const t = typeof v === 'number' && Number.isFinite(v) ? String(v) : v;
  return typeof t === 'string' && t.trim() ? t.trim().slice(0, max) : undefined;
}

/** `validateSearch` da rota de entrada. O router faz JSON.parse da URL: um id numerico chega como numero. Valor invalido e descartado. */
export function lerBuscaFicha(bruto: Record<string, unknown>): BuscaFicha {
  const itemId = textoDaUrl(bruto.itemId, 64);
  const q = textoDaUrl(bruto.q, 100);
  const situacao = SITUACOES_PAINEL_FICHA.find((s) => s === bruto.situacao);
  const tipo = TIPOS_PAINEL_FICHA.find((t) => t === bruto.tipo);
  const categoriaId = textoDaUrl(bruto.categoriaId, 64);
  const pagina = Number(bruto.pagina);
  return {
    ...(itemId && ID_SEGURO.test(itemId) ? { itemId } : {}),
    ...(q ? { q } : {}),
    ...(situacao ? { situacao } : {}),
    ...(tipo ? { tipo } : {}),
    ...(categoriaId && ID_SEGURO.test(categoriaId) ? { categoriaId } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
  };
}

/** Filtros da lista (tudo menos a pagina e o item): valem para a consulta do painel e para o relatorio. */
export function paraFiltroFichas(b: BuscaFicha): Pick<BuscaFicha, 'q' | 'situacao' | 'categoriaId' | 'tipo'> {
  return {
    ...(b.q ? { q: b.q } : {}),
    ...(b.situacao ? { situacao: b.situacao } : {}),
    ...(b.categoriaId ? { categoriaId: b.categoriaId } : {}),
    ...(b.tipo ? { tipo: b.tipo } : {}),
  };
}

/** Query do GET /fichas-tecnicas/painel para a pagina da busca. */
export function paraConsultaPainel(b: BuscaFicha) {
  const pagina = b.pagina ?? 1;
  return { limit: TAMANHO_PAGINA_FICHAS, offset: (pagina - 1) * TAMANHO_PAGINA_FICHAS, ...paraFiltroFichas(b) };
}

export function temFiltroFicha(b: BuscaFicha): boolean {
  return Boolean(b.q || b.situacao || b.categoriaId || b.tipo);
}
